import { Component } from '@theme/component';
import { MORPH_OPTIONS, morph } from '@theme/morph';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

const SUBMISSION_TIMEOUT_MS = 10000;

/**
 * @typedef {object} BackInStockFormElements
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} newsletterOptIn
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} error
 * @property {HTMLElement} success
 */

/**
 * Collects a back-in-stock request for the currently selected product variant.
 *
 * @extends Component
 */
class BackInStock extends Component {
  #abortController = new AbortController();

  #selectionGeneration = 0;

  #submitting = false;

  /** @returns {BackInStockFormElements | null} */
  #getFormElements() {
    const form = this.querySelector('[ref="form"]');
    const email = this.querySelector('[ref="email"]');
    const newsletterOptIn = this.querySelector('[ref="newsletterOptIn"]');
    const submitButton = this.querySelector('[ref="submitButton"]');
    const error = this.querySelector('[ref="error"]');
    const success = this.querySelector('[ref="success"]');

    if (
      !(form instanceof HTMLFormElement) ||
      !(email instanceof HTMLInputElement) ||
      !(newsletterOptIn instanceof HTMLInputElement) ||
      !(submitButton instanceof HTMLButtonElement) ||
      !(error instanceof HTMLElement) ||
      !(success instanceof HTMLElement)
    ) {
      return null;
    }

    return { form, email, newsletterOptIn, submitButton, error, success };
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const eventTarget = this.closest('.shopify-section, dialog');

    eventTarget?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    const generation = ++this.#selectionGeneration;

    try {
      const { detail } = await event.promise;
      if (generation !== this.#selectionGeneration || !detail?.html) return;
      if (detail.productId && detail.productId !== this.dataset.productId) return;

      const selector = `back-in-stock[data-block-id="${this.dataset.blockId}"]`;
      const replacement = detail.html.querySelector(selector);
      if (!(replacement instanceof HTMLElement)) return;

      morph(this, replacement, {
        ...MORPH_OPTIONS,
        childrenOnly: false,
      });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[back-in-stock] Variant update failed:', error);
      }
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const elements = this.#getFormElements();
    if (
      !elements ||
      this.#submitting ||
      this.dataset.active !== 'true' ||
      !elements.form.reportValidity()
    ) {
      return;
    }

    const email = elements.email.value.trim().toLowerCase();
    const sdk = [window.exponea, window.brweb].find(
      (candidate) => typeof candidate?.track === 'function' && typeof candidate?.update === 'function'
    );

    this.#setError(false);

    if (!sdk) {
      this.#setError(true);
      return;
    }

    this.#setSubmitting(true);

    const properties = {
      product_id: this.dataset.productId,
      product_name: this.dataset.productName,
      product_handle: this.dataset.productHandle,
      product_url: this.dataset.productUrl,
      product_image_url: this.dataset.productImageUrl,
      variant_id: this.dataset.variantId,
      variant_name: this.dataset.variantName,
      variant_sku: this.dataset.variantSku,
      price: Number(this.dataset.price),
      currency: this.dataset.currency,
      email,
      newsletter_opt_in: elements.newsletterOptIn.checked,
    };

    try {
      sdk.update({ email });
      await this.#track(sdk, this.dataset.eventName || 'back_in_stock_subscription', properties);
      const currentElements = this.#getFormElements();
      if (currentElements) {
        currentElements.form.hidden = true;
        currentElements.success.hidden = false;
        currentElements.success.focus();
      }
    } catch {
      console.warn('[back-in-stock] Subscription failed');
      this.#setError(true);
    } finally {
      this.#setSubmitting(false);
    }
  };

  /**
   * @param {{track: Function}} sdk
   * @param {string} eventName
   * @param {Record<string, string | number | boolean | undefined>} properties
   * @returns {Promise<void>}
   */
  #track(sdk, eventName, properties) {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error('Bloomreach tracking timed out')), SUBMISSION_TIMEOUT_MS);

      sdk.track(
        eventName,
        properties,
        () => {
          window.clearTimeout(timeout);
          resolve();
        },
        (error) => {
          window.clearTimeout(timeout);
          reject(error instanceof Error ? error : new Error('Bloomreach tracking failed'));
        }
      );
    });
  }

  /** @param {boolean} submitting */
  #setSubmitting(submitting) {
    this.#submitting = submitting;
    const elements = this.#getFormElements();
    if (!elements) return;

    elements.submitButton.disabled = submitting;
    elements.submitButton.setAttribute('aria-busy', String(submitting));
  }

  /** @param {boolean} visible */
  #setError(visible) {
    const elements = this.#getFormElements();
    if (elements) elements.error.hidden = !visible;
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
